import { useEffect, useState } from "react"

import { Alert, Flex, Form, message, Space, Tooltip, Typography, Upload } from "antd"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"

import { getCreditRequestAttemptsMeta } from "../../Containers/AlertsPage/components/OverviewInfo/utils"
import { AttachmentsList } from "../../Containers/CreditsPage/components/Attachments"
import { useExternalDemoMode } from "../../hooks/useExternalDemoMode"
import { useIncognito } from "../../hooks/useIncognito"
import Icon from "../../Icon"
import AppUtils, { creditRequestOptions } from "../../Utils/utils"
import ButtonComponent from "../Button"
import InputComponent from "../Input"
import TextAreaComponent from "../Input/TextArea"
import Private from "../Private"
import SelectComponent from "../Select"

const { Text, Link } = Typography

const MAX_TOTAL_SIZE_MB = 10
const MAX_TOTAL_FILES_AMOUNT = 20

const validationFields = { file_list: "Please attach at least one file as proof for your request." }

// Stands in for a read-only input while Incognito mode is on. `Form.Item` still owns the field and
// injects its `value` here, so the value stays in the submitted payload — only its rendering changes.
const MaskedFieldValue = ({ value }) => (
  <div className="wrap-like-input">
    <Private>{value}</Private>
  </div>
)

function CreditRequestForm({
  onClose,
  onSubmit,
  initialValues,
  fullHeight,

  isDisabledSubmitBtn,
  isSubmitting = false,
  isNewRequest = false,
  onReasonChange = () => {}
}) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const values = Form.useWatch([], form)
  const externalDemoMode = useExternalDemoMode()
  const incognito = useIncognito()

  const [uploadedFiles, setUploadedFiles] = useState([])
  const [attachmentsError, setAttachmentsError] = useState("")

  useEffect(() => {
    onReasonChange(values?.request_reason)
  }, [values?.request_reason, onReasonChange])

  const isManual = initialValues?.["request_type"]?.toLowerCase() === "manual"
  const isSystem = initialValues?.["request_type"]?.toLowerCase() === "system"
  const isRejected = initialValues?.["request_status"]?.toLowerCase() === "rejected"
  const systemRequiresProof = isSystem && initialValues?.["request_status"] === "Requires proof"

  const requiredFields = isSystem
    ? ["file_list"]
    : isManual && isRejected
      ? ["request_reason"]
      : ["request_reason", "file_list"]
  const shouldBeValid = requiredFields?.map((v) => values?.[v]).every((v) => !!v)
  const isValidData = shouldBeValid

  const availableCountFiles = MAX_TOTAL_FILES_AMOUNT - (initialValues?.["attachments"]?.length || 0)
  const currentResubmitAttempt = initialValues?.enabled_for_resubmit?.resubmit_attempts ?? 0
  const creditRequestAttemptsMeta = getCreditRequestAttemptsMeta(initialValues)
  const exceededResubmitAttempts = currentResubmitAttempt > 3
  const canReopenRequest = initialValues?.enabled_for_resubmit?.status
  const isReopenDisabled = !canReopenRequest && !isNewRequest && isManual
  const firstRejectedDaysDiff = dayjs().diff(dayjs.utc(initialValues?.first_rejected_at), "day")
  const isReopenTimeExpired = firstRejectedDaysDiff >= 10 && isManual

  const isFormDisabled =
    exceededResubmitAttempts ||
    isReopenDisabled ||
    externalDemoMode ||
    isDisabledSubmitBtn ||
    (isSystem && !systemRequiresProof)

  const isSubmitFormBtnDisabled = !isValidData || isFormDisabled

  const beforeUpload = (file, fileList) => {
    if (initialValues?.["attachments"]?.length === MAX_TOTAL_FILES_AMOUNT) {
      setAttachmentsError(`You can upload a maximum of ${MAX_TOTAL_FILES_AMOUNT} files.`)
      return Upload.LIST_IGNORE
    }

    const allowedExtensions = [
      "png",
      "jpeg",
      "jpg",
      "pdf",
      "txt",
      "doc",
      "docx",
      "odt",
      "rtf",
      "xls",
      "xlsx"
    ]
    const fileExtension = file?.name.split(".").pop().toLowerCase()
    if (!allowedExtensions.includes(fileExtension)) {
      message.error(`${file.name} has an unsupported format.`)
      return Upload.LIST_IGNORE
    }

    // Calculate the total weight: previously uploaded files + new file
    const currentTotalSize = [...uploadedFiles, file].reduce(
      (acc, currFile) => acc + (currFile.size || 0),
      0
    )
    if (currentTotalSize / 1024 / 1024 > MAX_TOTAL_SIZE_MB) {
      message.error(`Total file size should not exceed ${MAX_TOTAL_SIZE_MB} MB.`)
      return Upload.LIST_IGNORE
    }
    const newAttachmentsAmount =
      uploadedFiles.length + (fileList?.length || 0) + (initialValues?.["attachments"]?.length || 0)
    if (newAttachmentsAmount > MAX_TOTAL_FILES_AMOUNT) {
      setAttachmentsError(`You can upload a maximum of ${MAX_TOTAL_FILES_AMOUNT} files.`)
    } else {
      setAttachmentsError("")
    }

    return false
  }

  const handleUploadChange = (info) => {
    setUploadedFiles(info.fileList)
  }

  const onFileRemove = (file) => {
    setAttachmentsError("")
  }

  const onFinish = (values) => {
    onSubmit(values)
  }

  function handleClearError(e, field) {
    form.setFields([
      {
        name: field,
        errors: []
      }
    ])
  }

  function onFocusHandler(e, fieldName) {
    const field = fieldName || e.target.name || e.target.id
    handleClearError(e, field)
  }

  const addErrorIcon = (error) => (
    <span>
      <Icon type={"alert_triangle_error"} /> {error}
    </span>
  )

  const validateMessages = {
    required: addErrorIcon("Required field was not filled.")
  }

  const normFile = (e) => {
    if (Array.isArray(e)) return e
    return e?.fileList
  }

  return (
    <Form
      className={"with-default-error"}
      onFinish={onFinish}
      validateTrigger="onSubmit"
      validateMessages={validateMessages}
      layout="vertical"
      form={form}
      {...(initialValues ? { initialValues } : {})}
    >
      <div style={!fullHeight ? {} : { height: "min(80vh, 624px)", overflow: "auto" }}>
        {initialValues?.["request_type"] && !isSystem && (
          <Form.Item name="request_type" label={t("Request type")}>
            <InputComponent name="request_type" disabled={true} onFocus={onFocusHandler} />
          </Form.Item>
        )}

        <Form.Item name="alert_id" label={t("Alert ID")}>
          {incognito ? (
            <MaskedFieldValue />
          ) : (
            <InputComponent name="alert_id" disabled={true} onFocus={onFocusHandler} />
          )}
        </Form.Item>
        {initialValues?.["transaction_id"]?.length > 0 && (
          <Form.Item name="transaction_id" label={t("Transaction ID")}>
            <div className={"wrap-like-input"}>
              {/* The links themselves are replaced by the mask, so no href survives to leak the
                  transaction ID through the browser status bar. */}
              <Private>
                <Flex vertical={true} gap={4}>
                  {initialValues?.["transaction_id"]?.map((id, index) => (
                    <Link
                      key={id}
                      href={
                        initialValues?.["transaction_link"]?.[index] ||
                        initialValues?.["transaction_url"]?.[index]
                      }
                      target="_blank"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {id}
                    </Link>
                  ))}
                </Flex>
              </Private>
            </div>
          </Form.Item>
        )}

        <Form.Item
          name="request_reason"
          label={t("Reason for credit request")}
          rules={[{ required: !isFormDisabled && !isSystem }]}
        >
          {!isFormDisabled && !isSystem ? (
            <SelectComponent
              size={"large"}
              options={creditRequestOptions}
              name="request_reason"
              disabled={isFormDisabled}
              placeholder="Select a reason..."
              onFocus={onFocusHandler}
            />
          ) : (
            <InputComponent name="request_reason" disabled={true} onFocus={onFocusHandler} />
          )}
        </Form.Item>

        {(!isFormDisabled || initialValues?.["credit_request_details"]) && (
          <Form.Item
            name="credit_request_details"
            label={
              <Space size={2}>
                <Text>{t("Additional details")}</Text>
                <Tooltip title="Provide an extended comment explaining the reason for requesting credits. If you selected “Other” in the previous field, additional details are required to proceed.">
                  <div onMouseDown={(e) => e.preventDefault()} onClick={(e) => e.preventDefault()}>
                    <Icon type="circle_info" style={{ fontSize: 16, color: "#7A7A83" }} />
                  </div>
                </Tooltip>
              </Space>
            }
            rules={[
              {
                required:
                  AppUtils.haveCommonFieldObjects({ request_reason: "Other" }, values) &&
                  !isFormDisabled
              }
            ]}
          >
            <TextAreaComponent
              className="no-hover-bg"
              placeholder="Add any details that support your request"
              name="credit_request_details"
              disabled={isFormDisabled}
              onFocus={onFocusHandler}
            />
          </Form.Item>
        )}

        {isRejected && (
          <>
            <Form.Item name="rejection_reason" label={t("Rejection reason")}>
              <div className="wrap-like-input">
                {`${initialValues?.["rejection_reason"]}${initialValues?.["rejection_reason_details"] ? `: ${initialValues?.["rejection_reason_details"]}` : ""}`}
              </div>
            </Form.Item>

            {canReopenRequest && (
              <Alert
                type="info"
                message={<Text>{creditRequestAttemptsMeta.formAlertMessage}</Text>}
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
          </>
        )}

        <Flex vertical gap={4} style={{ marginBottom: 8 }}>
          <Text>
            {t("Attach files")}
            {!initialValues?.["attachments"]?.length && !isFormDisabled && (
              <Text type="danger">{" *"}</Text>
            )}
          </Text>
          <Text type={"secondary"} style={{ fontWeight: 300 }}>
            Possible applicable file formats: .png, .jpeg, .jpg, .pdf, .txt, .doc, .docx, .odt,
            .rtf, .xls, .xlsx
          </Text>
        </Flex>

        <Form.Item
          label={null}
          name="file_list"
          valuePropName="fileList"
          getValueFromEvent={normFile}
          rules={[
            {
              required: initialValues?.["attachments"]?.length ? false : true,
              message: t(validationFields["file_list"])
            }
          ]}
        >
          <Upload
            multiple
            maxCount={availableCountFiles}
            accept={".png,.jpeg,.jpg,.pdf,.txt,.doc,.docx,.odt,.rtf,.xls,.xlsx"}
            beforeUpload={beforeUpload}
            onChange={handleUploadChange}
            onRemove={onFileRemove}
            listType="text"
            disabled={availableCountFiles <= 0 || isFormDisabled}
            style={{ width: "100%" }}
            iconRender={(file) => (
              <Icon
                type={file?.type?.startsWith("image/") ? "pictures" : "document"}
                style={{ fontSize: 20, lineHeight: 1 }}
              />
            )}
            showUploadList={{
              extra: ({ size = 0 }) => (
                <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
                  ({(size / 1000).toFixed(0)}KB)
                </Text>
              ),
              showRemoveIcon: true,
              removeIcon: <Icon type={"delete"} style={{ fontSize: 20, lineHeight: 1 }} />
            }}
          >
            <ButtonComponent
              size="large"
              type="dashed"
              icon={<Icon type="arrow_out_of_box" />}
              disabled={attachmentsError || isFormDisabled}
              className={"dashed-btn table-action-btn flex center"}
              onClick={(e) => {
                if (uploadedFiles.length >= availableCountFiles) {
                  e.stopPropagation()
                  setAttachmentsError(
                    `You can upload a maximum of ${MAX_TOTAL_FILES_AMOUNT} files.`
                  )
                }
              }}
              block
            >
              <Text>{t("Upload files")}</Text>
            </ButtonComponent>
            {attachmentsError ? <Text type="danger">{attachmentsError}</Text> : <></>}
          </Upload>
        </Form.Item>
        <AttachmentsList attachments={initialValues?.["attachments"]} />

        <Text type="secondary" style={{ fontSize: 12 }}>
          {t("For assistance, please contact")}
        </Text>
        <Link onClick={() => window?.Intercom("show")} style={{ fontSize: 12, paddingLeft: 4 }}>
          {t("Chargeback Support.")}
        </Link>
        {isReopenTimeExpired && (
          <div>
            <Text type="danger" style={{ fontSize: 12 }}>
              Reopening period (10 days) has expired.
            </Text>
          </div>
        )}
      </div>
      <Flex gap={16} justify="end" style={{ padding: "32px 3px 3px 3px" }}>
        <ButtonComponent size="large" htmlType="button" onClick={onClose}>
          {t("Cancel")}
        </ButtonComponent>

        <Tooltip
          title={
            exceededResubmitAttempts
              ? t("Maximum resubmit attempts exceeded")
              : isDisabledSubmitBtn
                ? t(
                    "This action is restricted by your User Management settings for the current business account."
                  )
                : null
          }
        >
          <ButtonComponent
            size="large"
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            disabled={isSubmitFormBtnDisabled}
          >
            {t(isManual || isRejected ? "Resubmit" : "Submit")}
          </ButtonComponent>
        </Tooltip>
      </Flex>
    </Form>
  )
}

export default CreditRequestForm
